import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiShield, FiX, FiMail, FiRefreshCw, FiTrash2, FiEdit2, FiUserPlus } from 'react-icons/fi';
import {
  getCoAdmins,
  inviteCoAdmin,
  updateCoAdminPermissions,
  revokeCoAdmin,
  revokeInvite,
  resendInvite,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../services/apiError';
import { formatDate, normalizeDateOnly } from '../../utils/dateUtils';
import styles from './CoAdminsPage.module.css';

// ── Permission config ──────────────────────────────────────────────────────────
const PERMISSION_MODULES = [
  { key: 'receipts',           label: 'Receipts' },
  { key: 'offerings',          label: 'Offerings' },
  { key: 'payments',           label: 'Payments' },
  { key: 'journalEntries',     label: 'Journal Entries' },
  { key: 'members',            label: 'Members & Families' },
  { key: 'donationCategories', label: 'Donation Categories' },
  { key: 'accountingSetup',    label: 'Accounting Setup' },
  { key: 'reports',            label: 'Reports' },
  { key: 'eventsNotices',      label: 'Events & Notices' },
  { key: 'utils',              label: 'Utilities' },
];

const LEVELS = ['none', 'view', 'manage'];

const EMPTY_PERMISSIONS = Object.fromEntries(PERMISSION_MODULES.map((m) => [m.key, 'none']));

// Build a human-readable permissions summary (only non-none entries)
const permSummary = (permissions) => {
  if (!permissions) return '—';
  const parts = PERMISSION_MODULES
    .filter((m) => permissions[m.key] && permissions[m.key] !== 'none')
    .map((m) => `${m.label}: ${permissions[m.key]}`);
  return parts.length ? parts.join(', ') : 'No permissions';
};

// ── PermissionMatrix ──────────────────────────────────────────────────────────
const PermissionMatrix = ({ permissions, onChange }) => (
  <div className={styles.matrixGrid}>
    {PERMISSION_MODULES.map((mod) => (
      <div key={mod.key} className={styles.matrixRow}>
        <span className={styles.matrixLabel}>{mod.label}</span>
        <select
          className={styles.matrixSelect}
          value={permissions[mod.key] || 'none'}
          onChange={(e) => onChange(mod.key, e.target.value)}
        >
          {LEVELS.map((lvl) => (
            <option key={lvl} value={lvl}>
              {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
            </option>
          ))}
        </select>
      </div>
    ))}
  </div>
);

// ── EditPermissionsModal ──────────────────────────────────────────────────────
const EditPermissionsModal = ({ coAdmin, activeDerasarId, onClose, onSaved }) => {
  const [permissions, setPermissions] = useState({ ...EMPTY_PERMISSIONS, ...(coAdmin.permissions || {}) });
  const [saving, setSaving] = useState(false);

  const handleChange = (key, value) => setPermissions((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCoAdminPermissions(coAdmin.membershipId, { derasarId: activeDerasarId, permissions });
      toast.success('Permissions updated');
      onSaved();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update permissions'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h3>Edit Permissions</h3>
            <p className={styles.modalSubtitle}>{coAdmin.name || coAdmin.email}</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><FiX size={16} /></button>
        </div>
        <PermissionMatrix permissions={permissions} onChange={handleChange} />
        <div className={styles.modalActions}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CoAdminsPage = () => {
  const { activeDerasarId, isDerasarOwner } = useAuth();

  const [coAdmins, setCoAdmins]           = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Invite form
  const [email, setEmail]           = useState('');
  const [name, setName]             = useState('');
  const [permissions, setPermissions] = useState({ ...EMPTY_PERMISSIONS });
  const [inviting, setInviting]     = useState(false);

  // Edit modal
  const [editingCoAdmin, setEditingCoAdmin] = useState(null);

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const res = await getCoAdmins(activeDerasarId);
      setCoAdmins(res.coAdmins || []);
      setPendingInvites(res.pendingInvites || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load co-admins'));
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Invite ─────────────────────────────────────────────────────────────────
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error('Email is required');
    setInviting(true);
    try {
      await inviteCoAdmin({ derasarId: activeDerasarId, email: email.trim(), name: name.trim() || undefined, permissions });
      toast.success('Invitation sent');
      setEmail('');
      setName('');
      setPermissions({ ...EMPTY_PERMISSIONS });
      await loadData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to send invitation'));
    } finally {
      setInviting(false);
    }
  };

  // ── Resend invite ──────────────────────────────────────────────────────────
  const handleResend = async (inviteId) => {
    setActionLoading(`resend-${inviteId}`);
    try {
      await resendInvite(inviteId, { derasarId: activeDerasarId });
      toast.success('Invitation resent');
      await loadData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to resend invitation'));
    } finally {
      setActionLoading(null);
    }
  };

  // ── Cancel invite ──────────────────────────────────────────────────────────
  const handleCancelInvite = async (inviteId) => {
    setActionLoading(`cancel-${inviteId}`);
    try {
      await revokeInvite(inviteId, activeDerasarId);
      toast.success('Invitation cancelled');
      await loadData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to cancel invitation'));
    } finally {
      setActionLoading(null);
    }
  };

  // ── Revoke co-admin ────────────────────────────────────────────────────────
  const handleRevoke = async (membershipId, memberName) => {
    if (!window.confirm(`Remove ${memberName || 'this co-admin'} as a co-admin? They will lose all co-admin access.`)) return;
    setActionLoading(`revoke-${membershipId}`);
    try {
      await revokeCoAdmin(membershipId, activeDerasarId);
      toast.success('Co-admin removed');
      await loadData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to remove co-admin'));
    } finally {
      setActionLoading(null);
    }
  };

  // ── Guard: owner only ──────────────────────────────────────────────────────
  if (!isDerasarOwner) {
    return (
      <div className={styles.container}>
        <div className={styles.guardMessage}>
          <FiShield size={32} className={styles.guardIcon} />
          <p>Only the derasar owner can manage co-admins.</p>
        </div>
      </div>
    );
  }

  if (!activeDerasarId) {
    return <div className="empty-state"><p>Select a derasar first.</p></div>;
  }

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <div className={styles.container}>
      <div className="page-header">
        <h2>Team / Co-Admins</h2>
      </div>

      {/* ── Invite Form ─────────────────────────────────────────────────── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <FiUserPlus size={15} />
          Invite a Co-Admin
        </div>

        <form onSubmit={handleInvite} className={styles.inviteForm}>
          <div className={styles.inviteFields}>
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                required
                placeholder="e.g. admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Name (optional)</label>
              <input
                type="text"
                placeholder="e.g. Rahul Shah"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className={styles.matrixTitle}>Permissions</label>
            <PermissionMatrix
              permissions={permissions}
              onChange={(key, val) => setPermissions((p) => ({ ...p, [key]: val }))}
            />
          </div>

          <div className={styles.inviteActions}>
            <button type="submit" className="btn btn-primary" disabled={inviting}>
              <FiMail size={14} />
              {inviting ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Pending Invites ──────────────────────────────────────────────── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <FiMail size={15} />
          Pending Invites
          <span className={styles.countBadge}>{pendingInvites.length}</span>
        </div>

        {pendingInvites.length === 0 ? (
          <div className="empty-state" style={{ padding: '20px 0' }}>
            <p>No pending invites</p>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Permissions</th>
                  <th>Expires</th>
                  <th className={styles.actionsCell}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((invite) => (
                  <tr key={invite.inviteId}>
                    <td className={styles.emailCell}>
                      <FiMail size={12} className={styles.rowIcon} />
                      {invite.email}
                    </td>
                    <td className={styles.permCell}>{permSummary(invite.permissions)}</td>
                    <td className={styles.dateCell}>
                      {invite.expiresAt ? formatDate(normalizeDateOnly(invite.expiresAt)) : '—'}
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.actionBtns}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleResend(invite.inviteId)}
                          disabled={actionLoading === `resend-${invite.inviteId}`}
                          title="Resend invitation"
                        >
                          <FiRefreshCw size={12} />
                          {actionLoading === `resend-${invite.inviteId}` ? 'Sending…' : 'Resend'}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleCancelInvite(invite.inviteId)}
                          disabled={actionLoading === `cancel-${invite.inviteId}`}
                          title="Cancel invitation"
                          style={{ color: 'var(--color-danger)' }}
                        >
                          <FiX size={12} />
                          {actionLoading === `cancel-${invite.inviteId}` ? 'Cancelling…' : 'Cancel'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Co-Admins ────────────────────────────────────────────────────── */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <FiShield size={15} />
          Co-Admins
          <span className={styles.countBadge}>{coAdmins.length}</span>
        </div>

        {coAdmins.length === 0 ? (
          <div className="empty-state" style={{ padding: '20px 0' }}>
            <p>No co-admins yet. Invite someone above.</p>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Permissions</th>
                  <th className={styles.actionsCell}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coAdmins.map((ca) => (
                  <tr key={ca.membershipId}>
                    <td className={styles.nameCell}>{ca.name || '—'}</td>
                    <td className={styles.emailCell}>
                      <FiMail size={12} className={styles.rowIcon} />
                      {ca.email}
                    </td>
                    <td className={styles.permCell}>{permSummary(ca.permissions)}</td>
                    <td className={styles.actionsCell}>
                      <div className={styles.actionBtns}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEditingCoAdmin(ca)}
                          title="Edit permissions"
                        >
                          <FiEdit2 size={12} /> Edit
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleRevoke(ca.membershipId, ca.name || ca.email)}
                          disabled={actionLoading === `revoke-${ca.membershipId}`}
                          title="Remove co-admin"
                          style={{ color: 'var(--color-danger)' }}
                        >
                          <FiTrash2 size={12} />
                          {actionLoading === `revoke-${ca.membershipId}` ? 'Removing…' : 'Remove'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Edit Permissions Modal ────────────────────────────────────────── */}
      {editingCoAdmin && (
        <EditPermissionsModal
          coAdmin={editingCoAdmin}
          activeDerasarId={activeDerasarId}
          onClose={() => setEditingCoAdmin(null)}
          onSaved={() => { setEditingCoAdmin(null); loadData(); }}
        />
      )}
    </div>
  );
};

export default CoAdminsPage;
